import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Router} from '@angular/router';
import {BehaviorSubject, firstValueFrom, lastValueFrom} from 'rxjs';
import {map} from 'rxjs/operators';
import {Apollo} from 'apollo-angular';
import {NgxPermissionsService} from 'ngx-permissions';

import {StorageService} from './storage.service';
import {NotificationService} from './notification.service';
import {SettingsService} from './settings.service';

import {environment} from '../../environments/environment';

import {GET_COMPANY_REPRESENTATIVE_PROFILE, GET_USER_PROFILE, ProfileObject} from '../guest/login/login.graphql';
import {
  GET_CITIZEN_PROFILE,
  GET_CITIZEN_PROFILE_UPGRADE_TO_SERVANT_CHECK,
  UPDATE_USER_PROFILE
} from '../guest/user-profile/user-profile.graphql';
import {UPDATE_INSTITUTION} from '../store/entities/system-settings/institution/institution.graphql';
import {UPDATE_INSTUTION_DESIGNATION} from '../store/entities/instution-settings/designation/designation.graphql';

import {Designation} from '../store/entities/instution-settings/designation/designation.model';
import {UpdatePublicServantComponent} from '../shared/components/update-public-servant/update-public-servant.component';

export interface AuthTokenModel {
  access_token: string;
  token_type: string;
  refresh_token: string;
  account_type: string;
  expires_in: number;
  scope: string;
  success?: string;
  error?: string;
  user?: any;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  perms: any;
  photoUrl$ = new BehaviorSubject<string>(undefined);

  constructor(
    private http: HttpClient,
    private router: Router,
    private permissionsService: NgxPermissionsService,
    private apollo: Apollo,
    private notificationService: NotificationService,
    private storageService: StorageService,
  ) {}

  private buildAuthHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
      Authorization: 'Basic ' + btoa(environment.CLIENT_ID + ':' + environment.CLIENT_SECRET)
    });
  }

  private storeTokenData(tokenDetails: AuthTokenModel): void {
    this.storageService.setItem('currentClient', tokenDetails.access_token);
    this.storageService.setItem('refreshToken', tokenDetails.refresh_token);
    this.storageService.setItem('expireTime', tokenDetails.expires_in);
    const expiry = Math.round(Date.now() / 1000) + tokenDetails.expires_in;
    this.storageService.setItem('expiry', expiry);
  }

  async login(username: string, password: string): Promise<AuthTokenModel> {
    const body = `username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}&grant_type=password`;
    const headers = this.buildAuthHeaders();

    return await lastValueFrom(
      this.http.post<AuthTokenModel>(`${environment.AUTH_SERVER_URL}oauth2/access_token`, body, { headers }).pipe(
        map(async (tokenDetails) => {
          if (tokenDetails?.access_token) {
            this.storeTokenData(tokenDetails);
            if (tokenDetails.user?.profile_type === 'COMPANY') {
              console.log("comany", tokenDetails.user)
              await this.loadCompanyProfile();
            } else {
              this.loadCitizenProfile(tokenDetails.user.uid)
              await this.authRole('LOGIN');
            }
          } else if (tokenDetails?.error || !tokenDetails?.success) {
            this.notificationService.errorMessage('Sorry! Wrong credentials');
          }
          return tokenDetails;
        })
      )
    );
  }

  async loadCompanyProfile() {
    const data: any = await this.apollo.query({
      query: GET_COMPANY_REPRESENTATIVE_PROFILE,
      fetchPolicy: 'network-only'
    }).pipe(
      this.notificationService.catchError('Profile Error'),
      map((response) => {
        let result = Object.values(response)[0]?.getCompanyRepresentativeProfile;
        if (result?.response?.status) {
          document.getElementById('chatwoot-sdk-script')?.remove();
          this.storageService.setItem('profile', result.data.representative);
          this.storageService.setItem('userType', 'COMPANY');
          this.storageService.setItem('role', result.data.representative.role);
          this.storageService.setItem('designation', result.data.representative.userDesignation);
          location.href = '/client-services';
        } else {
          this.notificationService.errorMessage(`${result?.response?.message} code: ${result?.response?.code}`);
        }
        return response;
      })
    ).toPromise();
    return data;
  }
  loadCitizenProfile(userUniqueUid){
    this.apollo
      .query({
        query: GET_CITIZEN_PROFILE_UPGRADE_TO_SERVANT_CHECK,
        variables:{profileUniqueId:userUniqueUid},
        fetchPolicy: 'network-only'
      })
      .pipe(
        this.notificationService.catchError(
          'Profile Error'
        ),
        map((data) => {
            if (data) {
              let result: any = Object.values(data)[0];
              result = result.getCitizenProfile
              if (result?.response?.status) {
                console.log(result.data)
                this.storageService.setItem('citizenType', result.data.citizenType);
                this.storageService.setItem('isPublicServant', result.data.citizenServant);
              }
            }
          }
        )).subscribe();
  }
  async refreshToken(): Promise<AuthTokenModel | undefined> {
    const token = this.storageService.getItem('currentClient');
    const refresh = this.storageService.getItem('refreshToken');
    if (!token || !refresh) return undefined;

    const body = `refresh_token=${encodeURIComponent(refresh)}&grant_type=refresh_token`;
    const headers = this.buildAuthHeaders();

    try {
      return await firstValueFrom(
        this.http.post<AuthTokenModel>(`${environment.AUTH_SERVER_URL}/oauth2/access_token`, body, { headers }).pipe(
          map((tokenDetails) => {
            if (tokenDetails?.access_token) {
              this.storeTokenData(tokenDetails);
              this.monitorRefreshToken();
            }
            return tokenDetails;
          })
        )
      );
    } catch (e) {
      console.error('Failed to get refresh token', e);
    }
    return undefined;
  }

  alreadyLoggedIn(): boolean {
    return !!this.storageService.getItem('currentClient');
  }
  isCompany(): boolean {
    return this.storageService.getItem('profileType') === 'COMPANY';
  }

  monitorRefreshToken(): void {
    const expiry: number = this.storageService.getItem('expiry');
    const refreshTime = expiry - Date.now();
    if (expiry && refreshTime > 10000) {
      const timeOut = setTimeout(() => {
        clearInterval(timeOut);
        if (this.alreadyLoggedIn()) this.refreshToken();
      }, refreshTime - 10000);
    }
  }

  logout(sessionExpired = false): void {
    this.permissionsService.flushPermissions();
    this.storageService.clearStorage();
    localStorage.clear();
    if (sessionExpired) {
      localStorage.setItem('urlToRedirect', this.router.url);
      this.storageService.setItem('sessionExpired', 'true');
    }
    location.href = '/login';
    this.photoUrl$.next(undefined);
  }

  async authRole(from: 'LOGIN' | 'OTHER' = 'OTHER') {
    this.permissionsService.loadPermissions([]);
    this.perms = this.storageService.getItem('currentClient');
    if (!this.perms) return;

    const data: any = await this.apollo.query({
      query: GET_USER_PROFILE,
      fetchPolicy: 'network-only'
    }).pipe(
      this.notificationService.catchError('Profile Error'),
      map((response) => {
        let result = Object.values(response)[0]?.getUserProfileAndRole;
        if (!result?.response?.status) {
          this.notificationService.errorMessage(`${result?.response?.message} code: ${result?.response?.code}`);
          return response;
        }

        document.getElementById('chatwoot-sdk-script')?.remove();
        this.storageService.setItem('profile', result.data.profile);
        this.storageService.setItem('role', result.data.role);
        this.storageService.setItem('designation', result.data.userDesignation);
        this.storageService.setItem('institution', result.data.institution);

        const profileType = result.data.profile?.profileType;
        this.storageService.setItem('userType', profileType);
        if (profileType === 'INST' || profileType === 'POPSM' || profileType === 'Executive') {
          const permissions = result.data.role.rolePermissions?.map((obj: any) => obj?.permissionCode) || [];
          permissions.push('NO_PERMISSION');
          this.permissionsService.loadPermissions(permissions);
          if (from === 'LOGIN') location.href = profileType === 'Executive' ? '/reports' : '/tgsd-landing';
          else this.monitorRefreshToken();
        } else if (profileType === 'CIT') {
          if (from === 'LOGIN') {
            const urlToRedirect = this.storageService.getItem('urlToRedirect');
            if (urlToRedirect) {
              this.storageService.removeItem('urlToRedirect');
              return this.router.navigateByUrl(urlToRedirect);
            }
            location.href = '/client-services';
          } else this.monitorRefreshToken();
        }
        return response;
      })
    ).toPromise();
    return data;
  }

  async updateUserProfile(profile: ProfileObject) {
    return await this.updateProfileData(UPDATE_USER_PROFILE, 'updateMyProfile', profile, 'profile');
  }

  async updateInstitution(institution: any) {
    return await this.updateProfileData(UPDATE_INSTITUTION, 'updateInstitutionMutation', institution, 'institution');
  }

  async updateDesignation(designation: Designation) {
    return await this.updateProfileData(UPDATE_INSTUTION_DESIGNATION, 'updateInstitutionDesignationMutation', designation, 'designation');
  }

  private async updateProfileData(mutation: any, mutationKey: string, input: any, storageKey: string) {
    const token = this.storageService.getItem('currentClient');
    if (!token) return;

    const data: any = await this.apollo.mutate({
      mutation,
      fetchPolicy: 'network-only',
      variables: { input }
    }).pipe(
      this.notificationService.catchError(`Update ${storageKey} Error`),
      map((response) => {
        let result = Object.values(response)[0]?.[mutationKey];
        if (result?.response?.status) {
          this.storageService.setItem(storageKey, result.data);
          this.notificationService.successMessage('Updated Successfully');
        } else {
          this.notificationService.errorMessage(`${result?.response?.message} code: ${result?.response?.code}`);
        }
        return response;
      })
    ).toPromise();
    return data;
  }

}
